import { jsPDF } from 'jspdf';
import { parseEducation } from './education';

// === Color palette (RGB arrays) ===
const C = {
  primary:     [55, 65, 133],
  primaryDark: [37, 44, 97],
  primaryLight:[99, 102, 241],
  gold:        [202, 138, 4],
  goldLight:   [253, 230, 138],
  white:       [255, 255, 255],
  black:       [17, 24, 39],
  gray800:     [31, 41, 55],
  gray700:     [55, 65, 81],
  gray500:     [107, 114, 128],
  gray400:     [156, 163, 175],
  gray300:     [209, 213, 219],
  gray200:     [229, 231, 235],
  gray100:     [243, 244, 246],
  gray50:      [249, 250, 251],
  blue:        [37, 99, 235],
  purple:      [124, 58, 237],
  emerald:     [5, 150, 105],
  indigo50:    [238, 242, 255],
  amber50:     [255, 251, 235],
};

const PW = 210; // page width
const PH = 297; // page height
const ML = 18;  // margin left
const MR = 18;  // margin right
const CW = PW - ML - MR; // content width

// --- Helpers ---
const fillRect = (d, x, y, w, h, c) => { d.setFillColor(...c); d.rect(x, y, w, h, 'F'); };
const fillRRect = (d, x, y, w, h, r, c) => { d.setFillColor(...c); d.roundedRect(x, y, w, h, r, r, 'F'); };

const badge = (d, txt, x, y, bg, fg, fs = 7.5) => {
  d.setFont('helvetica', 'bold');
  d.setFontSize(fs);
  const tw = d.getTextWidth(txt);
  const pw = tw + 7;
  const ph = 5.5;
  fillRRect(d, x, y, pw, ph, 2.75, bg);
  d.setTextColor(...fg);
  d.text(txt, x + 3.5, y + 4);
  return pw + 3;
};

const sectionTitle = (d, title, y, maxW, accentColor = C.primary) => {
  // Thick accent bar
  fillRRect(d, ML, y, 3, 7.5, 1.5, accentColor);
  d.setFont('helvetica', 'bold');
  d.setFontSize(10);
  d.setTextColor(...C.gray800);
  d.text(title, ML + 7, y + 5.5);
  // Thin line spanning only the column
  d.setDrawColor(...C.gray300);
  d.setLineWidth(0.2);
  d.line(ML, y + 9.5, ML + maxW, y + 9.5);
  return y + 15;
};

const loadImage = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const c = document.createElement('canvas');
    const s = 240;
    c.width = s; c.height = s;
    const ctx = c.getContext('2d');
    ctx.beginPath(); ctx.arc(s/2, s/2, s/2, 0, Math.PI*2); ctx.clip();
    const sc = Math.max(s / img.width, s / img.height);
    const w = img.width * sc, h = img.height * sc;
    ctx.drawImage(img, (s-w)/2, (s-h)/2, w, h);
    resolve(c.toDataURL('image/png'));
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

// ================================================================
// MAIN: Generate Alumni CV PDF
// ================================================================
export const generateAlumniCV = async (alumni) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const edu = parseEducation(alumni.education);

  // ===============================================================
  // HEADER BLOCK
  // ===============================================================
  // Dark bg
  fillRect(doc, 0, 0, PW, 68, C.primaryDark);
  // Lighter bottom strip
  fillRect(doc, 0, 46, PW, 22, C.primary);
  // Gold accent line
  fillRect(doc, 0, 68, PW, 2, C.gold);

  // --- Photo ---
  const cx = ML + 18, cy = 30, r = 16;
  doc.setFillColor(...C.white);
  doc.circle(cx, cy, r + 2, 'F');

  let hasPhoto = false;
  if (alumni.photo_url) {
    try {
      const b64 = await loadImage(alumni.photo_url);
      if (b64) {
        doc.addImage(b64, 'PNG', cx - r, cy - r, r * 2, r * 2);
        hasPhoto = true;
      }
    } catch { /* use initials */ }
  }
  if (!hasPhoto) {
    doc.setFillColor(...C.primary);
    doc.circle(cx, cy, r, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(...C.white);
    doc.text((alumni.name || '?').charAt(0).toUpperCase(), cx, cy + 3.5, { align: 'center' });
  }

  // --- Name ---
  const tx = cx + r + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...C.white);
  doc.text(alumni.name || 'Alumni PIMNAS', tx, 25);

  // --- Subtitle ---
  const sub = [alumni.job, alumni.institution].filter(Boolean).join('  |  ');
  if (sub) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(190, 195, 220);
    doc.text(sub, tx, 33);
  }

  // --- Header badges ---
  let bx = tx;
  if (alumni.tahun_pimnas) {
    bx += badge(doc, 'PIMNAS ke-' + alumni.tahun_pimnas, bx, 38, C.gold, C.white, 8);
  }
  if (edu.s1_kampus) {
    badge(doc, edu.s1_kampus, bx, 38, [80, 90, 160], C.white, 8);
  }

  // --- Top-right label ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(160, 165, 185);
  doc.text('CURRICULUM VITAE', PW - MR, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text('Alumni PIMNAS Indonesia', PW - MR, 14, { align: 'right' });

  // ===============================================================
  // CONTACT BAR
  // ===============================================================
  let y = 77;
  const contacts = [];
  if (alumni.email) contacts.push('Email: ' + alumni.email);
  if (alumni.linkedin) contacts.push('LinkedIn: ' + alumni.linkedin.replace(/https?:\/\/(www\.)?/i, ''));
  if (alumni.google_scholar) contacts.push('Google Scholar');

  if (contacts.length > 0) {
    fillRRect(doc, ML, y, CW, 9, 3, C.gray100);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.gray700);
    doc.text(contacts.join('     |     '), ML + 5, y + 6);
    y += 14;
  }

  // ===============================================================
  // TWO-COLUMN LAYOUT
  // ===============================================================
  const leftW = CW * 0.59;
  const gap = CW * 0.04;
  const rightW = CW * 0.37;
  const rightX = ML + leftW + gap;
  let yL = y, yR = y;

  // ================ LEFT COLUMN ================

  // --- PENDIDIKAN ---
  const hasEdu = edu.s1_kampus || edu.s1_jurusan || edu.s2_kampus || edu.s2_jurusan || edu.s3_kampus || edu.s3_jurusan;
  if (hasEdu) {
    yL = sectionTitle(doc, 'PENDIDIKAN', yL, leftW);

    const eduEntries = [
      { level: 'S1', kampus: edu.s1_kampus, jurusan: edu.s1_jurusan, color: C.primary, note: 'Almamater PIMNAS' },
      { level: 'S2', kampus: edu.s2_kampus, jurusan: edu.s2_jurusan, color: C.blue, note: null },
      { level: 'S3', kampus: edu.s3_kampus, jurusan: edu.s3_jurusan, color: C.purple, note: null },
    ].filter(e => e.kampus || e.jurusan);

    const timeX = ML + 5;       // timeline x
    const txtX = ML + 20;       // text block x (after badge area)
    const txtMaxW = leftW - 24; // max text width

    eduEntries.forEach((entry, idx) => {
      const isLast = idx === eduEntries.length - 1;
      // --- Row background card ---
      const rowH = entry.jurusan ? (entry.note ? 22 : 18) : (entry.note ? 17 : 13);
      fillRRect(doc, ML + 1, yL - 1, leftW - 2, rowH, 3, C.gray50);

      // --- Timeline dot ---
      doc.setFillColor(...entry.color);
      doc.circle(timeX, yL + 3, 2, 'F');
      // White inner dot
      doc.setFillColor(...C.white);
      doc.circle(timeX, yL + 3, 0.8, 'F');

      // --- Timeline connector line ---
      if (!isLast) {
        doc.setDrawColor(...C.gray300);
        doc.setLineWidth(0.4);
        doc.line(timeX, yL + 5.5, timeX, yL + rowH + 1);
      }

      // --- Level badge (S1/S2/S3) ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      const bw = doc.getTextWidth(entry.level) + 5;
      fillRRect(doc, timeX + 4.5, yL + 0.5, bw, 5, 2.5, entry.color);
      doc.setTextColor(...C.white);
      doc.text(entry.level, timeX + 7, yL + 4);

      // --- University name ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(...C.black);
      doc.text(entry.kampus || '-', txtX, yL + 4);
      yL += 8;

      // --- Jurusan ---
      if (entry.jurusan) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...C.gray500);
        const jLines = doc.splitTextToSize(entry.jurusan, txtMaxW);
        jLines.forEach(line => {
          doc.text(line, txtX, yL);
          yL += 4;
        });
        yL += 0.5;
      }

      // --- Note (e.g. Almamater PIMNAS) ---
      if (entry.note) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6.5);
        doc.setTextColor(...C.gold);
        doc.text(entry.note, txtX, yL);
        yL += 4;
      }

      yL += 3;
    });

    yL += 2;
  }

  // --- KARIR ---
  if (alumni.job || alumni.institution) {
    yL = sectionTitle(doc, 'KARIR & PEKERJAAN', yL, leftW);

    if (alumni.job) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...C.black);
      doc.text(alumni.job, ML + 8, yL + 1);
      yL += 6.5;
    }
    if (alumni.institution) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...C.gray500);
      doc.text(alumni.institution, ML + 8, yL + 1);
      yL += 6.5;
    }
    yL += 5;
  }

  // --- MINAT RISET ---
  if (alumni.research_interests) {
    yL = sectionTitle(doc, 'MINAT RISET & KEAHLIAN', yL, leftW, C.emerald);

    const interests = alumni.research_interests.split(',').map(s => s.trim()).filter(Boolean);
    let ix = ML + 5, iy = yL;
    const maxIx = ML + leftW - 5;

    interests.forEach(t => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const tw = doc.getTextWidth(t) + 8;
      if (ix + tw > maxIx) { ix = ML + 5; iy += 8.5; }
      fillRRect(doc, ix, iy, tw, 6, 3, C.indigo50);
      doc.setTextColor(...C.primary);
      doc.text(t, ix + 4, iy + 4.2);
      ix += tw + 3;
    });
    yL = iy + 14;
  }

  // --- PROFIL ONLINE (left column, if space) ---
  if (alumni.linkedin || alumni.google_scholar) {
    yL = sectionTitle(doc, 'PROFIL ONLINE', yL, leftW, C.blue);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);

    if (alumni.linkedin) {
      doc.setTextColor(...C.gray500);
      doc.text('LinkedIn:', ML + 8, yL + 1);
      doc.setTextColor(...C.blue);
      const url = alumni.linkedin.replace(/https?:\/\/(www\.)?/i, '');
      const lines = doc.splitTextToSize(url, leftW - 30);
      lines.forEach((line, i) => {
        doc.text(line, ML + 28, yL + 1 + i * 4);
      });
      yL += 4 + (lines.length - 1) * 4 + 3;
    }

    if (alumni.google_scholar) {
      doc.setTextColor(...C.gray500);
      doc.text('Scholar:', ML + 8, yL + 1);
      doc.setTextColor(...C.blue);
      const url = alumni.google_scholar.replace(/https?:\/\/(www\.)?/i, '');
      const lines = doc.splitTextToSize(url, leftW - 30);
      lines.forEach((line, i) => {
        doc.text(line, ML + 28, yL + 1 + i * 4);
      });
      yL += 4 + (lines.length - 1) * 4 + 3;
    }
    yL += 3;
  }

  // ================ RIGHT COLUMN ================

  // --- PIMNAS CARD ---
  if (alumni.tahun_pimnas) {
    const cardH = 32;
    fillRRect(doc, rightX, yR, rightW, cardH, 5, C.amber50);
    // Gold top accent
    doc.setFillColor(...C.gold);
    doc.roundedRect(rightX, yR, rightW, 5, 5, 5, 'F');
    doc.rect(rightX, yR + 3, rightW, 2, 'F'); // flatten bottom

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.gold);
    doc.text('PARTISIPASI PIMNAS', rightX + 6, yR + 13);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...C.black);
    doc.text('ke-' + alumni.tahun_pimnas, rightX + 6, yR + 25);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...C.gray500);
    doc.text('Pekan Ilmiah Mahasiswa Nasional', rightX + 6, yR + 29.5);

    yR += cardH + 6;
  }

  // --- KONTAK CARD ---
  const cDetails = [];
  if (alumni.email) cDetails.push({ label: 'Email', value: alumni.email });
  if (alumni.linkedin) {
    cDetails.push({ label: 'LinkedIn', value: alumni.linkedin.replace(/https?:\/\/(www\.)?/i, '') });
  }
  if (alumni.google_scholar) cDetails.push({ label: 'Google Scholar', value: alumni.google_scholar.replace(/https?:\/\/(www\.)?/i, '') });

  if (cDetails.length > 0) {
    // Calculate height
    doc.setFontSize(7.5);
    let calcH = 12;
    cDetails.forEach(item => {
      const lines = doc.splitTextToSize(item.value, rightW - 14);
      calcH += 5 + lines.length * 4 + 3;
    });

    fillRRect(doc, rightX, yR, rightW, calcH, 5, C.gray50);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.primaryDark);
    doc.text('KONTAK & PROFIL', rightX + 6, yR + 8);

    let cy = yR + 15;
    cDetails.forEach(item => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...C.gray500);
      doc.text(item.label, rightX + 6, cy);
      cy += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...C.primary);
      const lines = doc.splitTextToSize(item.value, rightW - 14);
      lines.forEach(line => { doc.text(line, rightX + 6, cy); cy += 4; });
      cy += 2;
    });

    yR += calcH + 6;
  }

  // --- RINGKASAN CARD ---
  const stats = [];
  if (hasEdu) {
    let deg = 'S1';
    if (edu.s3_kampus || edu.s3_jurusan) deg = 'S3';
    else if (edu.s2_kampus || edu.s2_jurusan) deg = 'S2';
    stats.push({ label: 'Pendidikan Terakhir', value: deg });
  }
  if (alumni.job) stats.push({ label: 'Profesi', value: alumni.job });
  if (alumni.research_interests) {
    const fields = alumni.research_interests.split(',').map(s => s.trim()).filter(Boolean);
    stats.push({ label: 'Bidang Keahlian', value: fields.join(', ') });
  }

  if (stats.length > 0) {
    // Pre-calculate height: each stat has label (7pt) + wrapped value lines
    doc.setFontSize(9);
    let calcSH = 10;
    stats.forEach(s => {
      const lines = doc.splitTextToSize(s.value, rightW - 14);
      calcSH += 5 + lines.length * 4.5 + 6;
    });
    const sH = calcSH;
    fillRRect(doc, rightX, yR, rightW, sH, 5, C.indigo50);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.primaryDark);
    doc.text('RINGKASAN', rightX + 6, yR + 8);

    let sy = yR + 16;
    stats.forEach(s => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...C.gray500);
      doc.text(s.label, rightX + 6, sy);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...C.black);
      const lines = doc.splitTextToSize(s.value, rightW - 14);
      lines.forEach(line => {
        doc.text(line, rightX + 6, sy + 5.5);
        sy += 4.5;
      });
      sy += 6;
    });

    yR += sH + 6;
  }

  // ===============================================================
  // FOOTER
  // ===============================================================
  const fy = PH - 14;
  doc.setDrawColor(...C.gray200);
  doc.setLineWidth(0.25);
  doc.line(ML, fy, PW - MR, fy);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.gray400);
  doc.text('Dokumen ini dibuat secara otomatis oleh Jejaring Alumni PIMNAS Indonesia', ML, fy + 5);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.gold);
  doc.text('alumni-pimnas.id', PW - MR, fy + 5, { align: 'right' });

  // Gold bottom bar
  fillRect(doc, 0, PH - 3, PW, 3, C.gold);

  // --- Save ---
  const safe = (alumni.name || 'alumni').replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  doc.save('CV_' + safe + '.pdf');
};
